import * as React from 'react';
import * as classNames from 'classnames';
import track, { TrackingProp } from 'react-tracking';
import * as I18n from 'i18n-js';

import ConditionDisplay from './condition_display';
import PriceWithShipping from './price_with_shipping';
import SellerInfo from './seller_info';
import SignupSigninModal from './signup_signin_modal';
import CoreLink from './core_link';
import {
  Listing,
  core_apimessages_Listing_ListingType,
} from '../rql';
import { IEventData } from '../tracker';
import AddToCartButton from './add_to_cart_button';
import CreateOfferButton from '../offers/create_offer_button';
import WatchBadge from './watch_badge';
import ImageCarousel from './image_carousel';
import {
  withUserContext,
  IUserContext,
} from './user_context_provider';
import { ITEM_WITH_SLUG_ROUTE } from '../url_helpers';
import { buildListingClickEvent } from '../elog/mparticle_tracker';
import BumpTag, { shouldShowBumpTag } from './bump_tag';
import { isExperimentEnabled } from '../user_context_helpers';
import { experiments, WEB } from '../constants';
import { formatExperimentsForEventAttributes } from '../elog/mparticle';

const DIGITAL_LISTING_UUID = 'e71b6e43-7217-4746-b4e3-64bf708a2664';
const LIMIT_LISTINGS_COUNT = 3;

interface IProps extends IUserContext {
  listing?: Listing;
  position?: number;
  inMobileApp?: boolean;
  displayMetadataAsOffer?: boolean;
  bumped?: boolean;
  onOfferSuccess?: Function;
  tracking?: TrackingProp;
  listingsCount?: number;
  limitListingCount?: boolean;
  wideRow?: boolean;
  onShowAllListings?: Function;
}

interface IState {
  modalOpen: boolean;
}

function PlaceholderCard() {
  return (
    <div className="listing-row-card listing-row-card--placeholder">
      <div className="listing-row-card__inner">
        <div className="listing-row-card__main">
          <div className="listing-row-card__info-and-image">
            <div className="listing-row-card__image">
              <div className="listing-row-card__image__inner" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function buildComponentName(data: IEventData, props: IProps): string {
  return props.bumped ? `${data.componentName}BumpRow` : data.componentName;
}

@track((props: IProps): IEventData => {
  return {
    mParticleInteraction: (data: IEventData) => {
      return {
        experiments: formatExperimentsForEventAttributes(props.user, [
          experiments.LISTING_CARD_NEW_TAB,
        ]),
        ...buildListingClickEvent({
          listing: props.listing,
          componentName: data.componentName,
          position: data.position,
          displayStyle: null,
          listingsCount: props.listingsCount,
        })
      }
    },
  };
})
export class ListingRowCard extends React.Component<IProps, IState> {
  state = {
    modalOpen: false,
  };

  isDigitalListing() {
    if (!this.props.listing.categoryUuids) { return false; }

    return this.props.listing.categoryUuids.includes(DIGITAL_LISTING_UUID);
  }

  listingUrl() {
    const { id, slug, bumpKey } = this.props.listing;

    return ITEM_WITH_SLUG_ROUTE.expand({
      slug,
      id,
      bk: bumpKey?.key,
    });
  }

  renderConditionDisplay() {
    if (this.isDigitalListing()) return null;

    return (
      <div className="listing-row-card__condition">
        <ConditionDisplay
          slug={this.props.listing.condition.conditionSlug}
          displayName={this.props.listing.condition.displayName}
        />
      </div>
    );
  }

  hasDiscount() {
    return !!this.props.listing.pricing.originalPrice;
  }

  renderMetadata() {
    if (this.props.displayMetadataAsOffer) {
      return (
        <div itemProp="offers" itemScope itemType="http://schema.org/Offer">
          <meta itemProp="priceCurrency" content={this.props.listing.pricing.buyerPrice.currency} />
          <meta itemProp="price" content={`${this.props.listing.pricing.buyerPrice.amountCents / 100}`} />
          <meta itemProp="availability" content="http://schema.org/InStock" />
          <meta itemProp="url" content={`http://reverb.com/${this.listingUrl()}`} />
          <meta itemProp="gtin14" content={this.props.listing.upc} />
        </div>
      );
    }

    return null;
  }

  getImageStyle() {
    if (!this.props.listing) return null;

    return {
      style: {
        backgroundImage: `url('${this.props.listing.images[0].source}')`,
      },
    };
  }

  acceptsOffers() {
    const { listing } = this.props;

    return listing.listingType === core_apimessages_Listing_ListingType.MAKE_AN_OFFER;
  }

  renderOfferButton() {
    const { listing } = this.props;

    if (!this.acceptsOffers()) { return null; }

    return (
      <CreateOfferButton
        listingId={listing.id}
        listing={listing}
        onOfferSuccess={this.props.onOfferSuccess}
        position={this.props.position}
        parentComponentName={buildComponentName(this.props.tracking.getTrackingData(), this.props)}
        bumpKey={listing.bumpKey}
      />
    );
  }

  renderWideRow() {
    const { listing } = this.props;
    const images = listing.images.map(i => (i.source));

    return (
      <>
        <div className="wide-listing-row__image-wrapper">
          <div className="bd-1 bd--offwhite width-100">
            <ImageCarousel
              images={images}
              imgAlt={listing.title}
            />
          </div>
        </div>
        <div className="wide-listing-row__content-wrapper">
          <div className="wide-listing-row__content">
            <div className="wide-listing-row__heading">
              {shouldShowBumpTag(listing) &&
                <div className="wide-listing-row__bump">
                  <BumpTag />
                  {listing.bumpInfo &&
                    <div className="mt-half opacity-60 size-60 weight-normal">
                      {I18n.t('discovery.bumpInfo', { ...listing.bumpInfo })}
                    </div>
                  }
                </div>
              }
              <div className="wide-listing-row__title">
                {listing.title}
              </div>
              <div className="wide-listing-row__condition">
                {this.renderConditionDisplay()}
              </div>
            </div>
            <div className="wide-listing-row__details">
              <div className="wide-listing-row__shop">
                <SellerInfo
                  listing={listing}
                  showBadges
                />
              </div>
              <div className="wide-listing-row__pricing">
                <PriceWithShipping
                  listing={listing}
                  pricePlusShipping
                />
              </div>
            </div>
          </div>
          <div className="wide-listing-row__actions">
            <div className="wide-listing-row__button-wrapper">
              <AddToCartButton
                listing={listing}
                position={this.props.position}
                parentComponentName={buildComponentName(this.props.tracking.getTrackingData(), this.props)}
              />
            </div>
            {this.acceptsOffers() &&
              <div className="wide-listing-row__button-wrapper">
                {this.renderOfferButton()}
              </div>
            }
            <div className="wide-listing-row__button-wrapper">
              <WatchBadge
                listing={listing}
                position={this.props.position}
                parentComponentName={buildComponentName(this.props.tracking.getTrackingData(), this.props)}
                renderAsButton
                includeText
                handleLoggedOutUser={() => this.setState({ modalOpen: true })}
              />
            </div>
          </div>
        </div>
      </>
    );
  }

  renderInner() {
    if (this.props.wideRow) {
      return this.renderWideRow();
    }

    return (
      <>
        {this.renderMetadata()}
        <div className="listing-row-card__main">
          <div className="listing-row-card__info-and-image">
            <div
              className="listing-row-card__image"
            >
              <div
                className="listing-row-card__image__inner"
                {...this.getImageStyle()}
              />
            </div>
            <div className="listing-row-card__info">
              {shouldShowBumpTag(this.props.listing) &&
                <div className="listing-row-card__bump-tag">
                  <BumpTag />
                </div>
              }
              <h4 className="listing-row-card__title">
                {this.props.listing.title}
                <div className="mt-half opacity-60 size-60 weight-normal">
                  {this.props.listing.bumpInfo &&
                    I18n.t('discovery.bumpInfo', { ...this.props.listing.bumpInfo })}
                </div>
              </h4>
              <SellerInfo
                listing={this.props.listing}
                showBadges
              />
            </div>
          </div>
          <div className="listing-row-card__pricing">
            <div className="listing-row-card__pricing__amount">
              <PriceWithShipping listing={this.props.listing} />
              {this.renderConditionDisplay()}
            </div>
            <div className="ml-2 align-center">
              <AddToCartButton
                listing={this.props.listing}
                className="listing-row-card__pricing__amount"
                position={this.props.position}
                parentComponentName={buildComponentName(this.props.tracking.getTrackingData(), this.props)}
              />
              <div className="mt-half">
                {this.renderOfferButton()}
              </div>
              <div className="mt-half">
                <WatchBadge
                  listing={this.props.listing}
                  position={this.props.position}
                  parentComponentName={buildComponentName(this.props.tracking.getTrackingData(), this.props)}
                  includeText
                  handleLoggedOutUser={() => this.setState({ modalOpen: true })}
                />
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  render() {
    if (!this.props.listing) { return <PlaceholderCard />; }

    const classes = classNames(
      'listing-row-card',
      { 'listing-row-card--software': this.isDigitalListing() },
      { 'listing-row-card--discounted': this.hasDiscount() },
    );

    const { user } = this.props;
    const openNewTab = user && user.deviceName === WEB && isExperimentEnabled(
      user,
      experiments.LISTING_CARD_NEW_TAB,
    );

    if (this.props.wideRow) {
      const wideRowClasses = classNames(
        'wide-listing-row',
        { 'wide-listing-row--featured': shouldShowBumpTag(this.props.listing) },
      );

      const wrapperClasses = classNames(
        'wide-listing-row__wrapper',
        { 'wide-listing-row__wrapper--truncated': this.props.limitListingCount && this.props.position === LIMIT_LISTINGS_COUNT },
        { 'wide-listing-row__wrapper--hidden': this.props.limitListingCount && this.props.position > LIMIT_LISTINGS_COUNT },
      );

      return (
        <div className={wrapperClasses}>
          {this.props.limitListingCount && this.props.position === LIMIT_LISTINGS_COUNT &&
            <button
              className="wide-listing-row__view-more"
              onClick={() => this.props.onShowAllListings()}
              type="button"
            >
              <div
                className="wide-listing-row__view-more__button"
              >
                {I18n.t('discovery.comparisonShoppingPages.viewMore')}
              </div>
            </button>
          }
          <CoreLink
            className={wideRowClasses}
            to={this.listingUrl()}
            entityId={this.props.listing.id}
            position={this.props.position}
            target={openNewTab ? '_blank' : undefined}
          >
            {this.renderInner()}
          </CoreLink>
          <SignupSigninModal
            onRequestClose={() => this.setState({ modalOpen: false })}
            active={this.state.modalOpen}
            trackSource="WatchBadge"
          />
        </div>
      );
    }

    return (
      <div className={classes}>
        <CoreLink
          className="listing-row-card__inner"
          to={this.listingUrl()}
          entityId={this.props.listing.id}
          position={this.props.position}
          target={openNewTab ? '_blank' : undefined}
        >
          {this.renderInner()}
        </CoreLink>
        <SignupSigninModal
          onRequestClose={() => this.setState({ modalOpen: false })}
          active={this.state.modalOpen}
          trackSource="WatchBadge"
        />
      </div>
    );
  }
}

export default withUserContext<IProps>(ListingRowCard);



// WEBPACK FOOTER //
// /home/reverb/workspace/core.deploy.production/build/src/node_modules/@reverbdotcom/commons/src/components/listing_row_card.tsx